import DeleteIcon from '@mui/icons-material/Delete';
import {
	Box,
	Grid,
	IconButton,
	Link as MaterialLink,
	Theme,
	Tooltip,
	Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData } from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import {
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import { Exposure, ExposureCreateRequest } from '../../../types/policy';
import { FastForm } from '../../fastForm';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import { getExposureName } from '../util_sharedEndorsementAndRenewal';
import AddExposureModal from './Exposures/AddExposureModal';

interface Props {
	authData: AuthData;
	renewalData: RenewalData;
	errorData: ErrorData;
}

const useStyles = makeStyles((theme: Theme) => ({
	titleWrapper: {
		display: 'flex',
		alignItems: 'flex-end',
	},
	title: {
		textTransform: 'capitalize',
		flexGrow: 1,
		fontSize: '18px',
	},
	auxDataLink: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
	},
	removeIcon: {
		position: 'relative',
		top: theme.spacing(1),
	},
}));

/**
 * The exposure detail page for an added exposure on an renewal.
 */
const AddedExposureDetails = (props: Props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const params = useParams();
	const classes = useStyles();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;

	const { renewalData, authData } = props;
	const {
		renewalUpdateObj,
		productConfig,
		setLoading,
		selectedExposure,
		fields: form,
		saveFastForm,
	} = renewalData;

	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	const [removedExposure, setRemovedExposure] = useState(null);

	const exposureCreateObj = selectedExposure as ExposureCreateRequest;

	const exposureName = exposureCreateObj.exposureName;
	const policyConfig = productConfig.policyConfiguration;
	const exposureConfig = policyConfig.exposures.find(
		(exposure) => exposure.name === exposureName,
	);

	const removeExposure = async () => {
		setIsConfirmModalOpen(false);
		setLoading(true);
		try {
			await saveFastForm(getValues(), dirtyFields, reset, {
				removeAddedExposure: { exposureLocator: removedExposure.locator },
			});
			successToast('Successfully removed exposure');
			navigate(
				`/policy/${params.policyId}/renewals/${params.renewalId}/exposures`,
			);
		} catch (error) {
			errorToast('Error removing exposure');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const renderRemoveExposureModal = () => {
		return (
			<ConfirmationModal
				entityName={
					(removedExposure as Exposure).name ||
					(removedExposure as ExposureCreateRequest).exposureName
				}
				entityType={t('exposure.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={() => removeExposure()}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const renderRemoveExposureIcon = () => {
		return (
			<Tooltip title={'Remove ' + t('exposure.singular.upper')}>
				<IconButton
					aria-label='Remove'
					className={classes.removeIcon}
					onClick={() => openConfirmationModal(selectedExposure as Exposure)}
				>
					<DeleteIcon />
				</IconButton>
			</Tooltip>
		);
	};

	const openConfirmationModal = (exposure: Exposure) => {
		setIsConfirmModalOpen(true);
		setRemovedExposure(exposure);
	};

	const renderActions = () => {
		return (
			<AddExposureModal
				policyConfig={policyConfig}
				authData={authData}
				renewalData={renewalData}
			/>
		);
	};

	const exposureDisplayName = getExposureName(
		exposureCreateObj,
		exposureConfig.displayName,
		renewalUpdateObj,
		null,
	);
	const isEditable =
		authData.hasPermission('renewal.update') &&
		renewalUpdateObj.state === 'application';
	const addedExposureIndex = renewalUpdateObj.addExposures.findIndex(
		(exposure) => exposure.locator === selectedExposure.locator,
	);

	return (
		<>
			<Box className={classes.titleWrapper}>
				<Typography className={classes.title}>{exposureDisplayName}</Typography>
				{authData.hasPermission('auxData.fetch') && (
					<MaterialLink
						className={classes.auxDataLink}
						component={Link}
						to={`auxData`}
					>
						Aux Data
					</MaterialLink>
				)}
				{isEditable && renderRemoveExposureIcon()}
				{isConfirmModalOpen && renderRemoveExposureModal()}
			</Box>
			<hr />
			<Grid container spacing={2}>
				<FastForm
					formName={`exposure-${addedExposureIndex}`}
					form={form}
					configVersion={props.renewalData.productConfig.configVersion}
					authData={props.authData}
					productName={productConfig.name}
					disabled={!isEditable}
					save={saveFastForm}
					setLoading={setLoading}
					disableExternalCalls={true}
					effectiveDate={renewalUpdateObj.overrideResourcesAsOfTimestamp}
				/>
			</Grid>
			{isEditable && renderActions()}
		</>
	);
};

export default withRenewalContext<Props>(AddedExposureDetails);
